import { DataState } from 'actions/data';
import { HttpClient } from 'api/HttpClient';
import { LoanPurpose } from 'enums/LoanPurpose';
import { SwimmingPoolType } from 'enums/SwimmingPoolType';
import { UnderwritingResult } from 'enums/UnderwritingResult';
import { Address } from 'api/ApplicationApi';
import { CurrentOwnershipStatus } from 'enums/ApplicationsVariables';
import { PossessionCheck } from 'actions/borrower';
import { BorrowerIncomeType, OtherIncomeTypes } from 'enums/BorrowerIncomeType';

export interface UnderwritingGeneralInfoDto extends DataState {
  loanAmount: number;
  loanPurpose: LoanPurpose;
  contractorPromoLinkUsed: boolean;
  IsBorrowerManualFlow?: boolean;
  IsCoBorrowerManualFlow?: boolean;
  BorrowerChangedInfoAfterProvePreFill?: boolean;
  CoBorrowerChangedInfoAfterProvePreFill?: boolean;
  proveErrorStatuses?: string;
  proveErrorDescriptions?: string;
  otherHomeImprovementType?: string;
  swimmingPoolType?: SwimmingPoolType;
}

export interface UnderwritingBorrowerDto {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  email: string;
  street_name: string;
  street_number: string;
  city: string;
  state_or_province: string;
  zip_or_postal_code: string;
  phone: string | null;
  personal_id_number: string;
  borrower_is_permanent_resident: boolean;
  borrower_prove_eligibility: boolean;
  possession_check: PossessionCheck;
  borrower_work_order_address_verified: Address;
  current_home_ownership: BorrowerOwnership;
  primary_address_ownership_status?: CurrentOwnershipStatus | null;
  move_in_date: string | null;
  borrower_prefilled_first_name?: string | null;
  borrower_prefilled_last_name?: string | null;
  borrower_prefilled_ssn?: string | null;
  borrower_prefilled_state?: string | null;
  borrower_prefilled_street_address?: string | null;
  borrower_prefilled_zip_or_postal_code?: string | null;
  borrower_prefilled_city?: string | null;
  borrower_previous_address?: Address | null;
  previous_move_in_date?: string | null;
}

export enum BorrowerOwnership {
  Buying = 'Buying',
  LiveWithParents = 'LiveWithParents',
  Own = 'Own',
  Rent = 'Rent',
}

export enum IncomeW2Type {
  Employee = 'Employee',
  BusinessOwner = 'Business Owner',
}

export interface UnderwritingCoborrowerDto {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  email: string;
  phone: string | null;
  personal_id_number: string;
  coborrower_prove_eligibility: boolean;
}

export interface UnderwritingBorrowerIncome {
  selectedIncomes: (BorrowerIncomeType | OtherIncomeTypes)[];
  incomes: {
    W2?: number | null;
    W2_second_employer?: number | null;
    W2_third_employer?: number | null;
    self_employed?: number | null;
    va_disability?: number | null;
    military_pension?: number | null;
    employer_pension?: number | null;
    social_security?: number | null;
    rental_income?: number | null;
    homemaker_unemployed?: number | null;
    other?: number | null;
    trust?: number | null;
    child_support?: number | null;
    alimony?: number | null;
    foster_care?: number | null;
    annuities?: number | null;
    term_disability?: number | null;
  };
  other: string;
  w2Incomes: {
    companyName1?: string;
    companyName2?: string;
    companyName3?: string;
    companyMappingId1?: string;
    companyMappingId2?: string;
    companyMappingId3?: string;
    incomeW2Type1?: IncomeW2Type;
    incomeW2Type2?: IncomeW2Type;
    incomeW2Type3?: IncomeW2Type;
  };
  total: number;
}

export interface UnderwritingIncomeDto {
  borrower: UnderwritingBorrowerIncome;
  coBorrower: UnderwritingBorrowerIncome;
}

export interface RunApplicationUnderwritingDto {
  borrowerData: UnderwritingBorrowerDto;
  coborrowerData: UnderwritingCoborrowerDto;
  generalData: UnderwritingGeneralInfoDto;
  incomeData: UnderwritingIncomeDto;
}

export interface ApplicationUnderwritingDto {
  applicationId: string | null;
  result: UnderwritingResult;
  email: string | null;
  borrowerId: string;
  coborrowerId?: string;
  applicationWasPrequalified: boolean;
}

export default class UnderwritingApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL || '');
  }

  public async runUnderwriting(data: RunApplicationUnderwritingDto): Promise<ApplicationUnderwritingDto> {
    return this.instance.post('/underwriting/customer-portal-request', data).then((response) => response.data);
  }
}

export const underwritingApi = new UnderwritingApi();
